import { useApiClient } from '@yuksalish/api-client'
import type {
  AttendanceFilters,
  AttendancePayload,
  AttendanceRecord,
  AttendanceReportRow,
  CurrencyOption,
  Employee,
  EmployeeCard,
  EmployeeCardFilters,
  EmployeeCardPayload,
  EmployeeFilters,
  EmployeePayload,
  FileFilters,
  Option,
  PagedResult,
  TimesheetEntry,
  TimesheetFilters,
  TimesheetPayload,
  TimesheetReportRow,
  UploadFilePayload,
  UploadedFile,
} from '../types/hr'
import { toApiFilePath } from '../utils/files'

interface ApiList<T> {
  data: T[]
  meta?: {
    page?: number
    current_page?: number
    per_page?: number
    total?: number
    total_pages?: number
    last_page?: number
  }
}

interface ApiItem<T> {
  data: T
}

const MAX_PAGES = 6

/** Agent roles arrive as a string array, but tolerate a comma separated string. */
function normalizeRoles(value: unknown): string[] {
  if (Array.isArray(value)) return value.map((role) => String(role).toLowerCase())
  if (typeof value === 'string' && value.trim() !== '') {
    return value.split(',').map((role) => role.trim().toLowerCase())
  }
  return []
}

function parseMeta(meta: ApiList<never>['meta'], fallbackPage = 1, fallbackPerPage = 20) {
  return {
    page: meta?.page ?? meta?.current_page ?? fallbackPage,
    per_page: meta?.per_page ?? fallbackPerPage,
    total: meta?.total ?? 0,
    total_pages: meta?.total_pages ?? meta?.last_page ?? 1,
  }
}

export function useHrApi() {
  const client = useApiClient()

  // --- Employees -----------------------------------------------------------------

  async function listEmployees(filters: EmployeeFilters = {}): Promise<PagedResult<Employee>> {
    const response = await client.get<ApiList<Employee>>('/employees', {
      params: {
        search: filters.search || undefined,
        department_agent_id: filters.department_agent_id || undefined,
        employee_type: filters.employee_type || undefined,
        is_active: filters.is_active === '' ? undefined : filters.is_active,
        page: filters.page ?? 1,
        per_page: filters.per_page ?? 20,
      },
    })
    return {
      items: response.data.data,
      meta: parseMeta(response.data.meta, filters.page ?? 1, filters.per_page ?? 20),
    }
  }

  /**
   * Walks the employee endpoint to collect every matching row. Used by the
   * overview and attendance views, which aggregate over the whole workforce.
   */
  async function listAllEmployees(filters: EmployeeFilters = {}): Promise<Employee[]> {
    const items: Employee[] = []
    let page = 1
    let totalPages = 1
    do {
      const result = await listEmployees({ ...filters, page, per_page: 100 })
      items.push(...result.items)
      totalPages = Math.max(1, result.meta.total_pages)
      page += 1
    } while (page <= totalPages && page <= MAX_PAGES)
    return items
  }

  async function getEmployee(id: number | string): Promise<Employee> {
    const response = await client.get<ApiItem<Employee>>(`/employees/${id}`)
    return response.data.data
  }

  async function createEmployee(payload: EmployeePayload): Promise<Employee> {
    const response = await client.post<ApiItem<Employee>>('/employees', payload)
    return response.data.data
  }

  async function updateEmployee(id: number | string, payload: Partial<EmployeePayload>): Promise<Employee> {
    const response = await client.put<ApiItem<Employee>>(`/employees/${id}`, payload)
    return response.data.data
  }

  async function deleteEmployee(id: number | string): Promise<void> {
    await client.delete(`/employees/${id}`)
  }

  // --- Employee access cards -----------------------------------------------------

  async function listEmployeeCards(filters: EmployeeCardFilters = {}): Promise<PagedResult<EmployeeCard>> {
    const response = await client.get<ApiList<EmployeeCard>>('/kiosk/cards', {
      params: {
        search: filters.search || undefined,
        employee_id: filters.employee_id || undefined,
        is_active: filters.is_active === '' ? undefined : filters.is_active,
        page: filters.page ?? 1,
        per_page: filters.per_page ?? 100,
      },
    })
    return {
      items: response.data.data,
      meta: parseMeta(response.data.meta, filters.page ?? 1, filters.per_page ?? 100),
    }
  }

  async function createEmployeeCard(payload: EmployeeCardPayload): Promise<EmployeeCard> {
    const response = await client.post<ApiItem<EmployeeCard>>('/kiosk/cards', payload)
    return response.data.data
  }

  async function updateEmployeeCard(id: number, payload: Partial<EmployeeCardPayload>): Promise<EmployeeCard> {
    const response = await client.put<ApiItem<EmployeeCard>>(`/kiosk/cards/${id}`, payload)
    return response.data.data
  }

  async function deleteEmployeeCard(id: number): Promise<void> {
    await client.delete(`/kiosk/cards/${id}`)
  }

  // --- Attendance ----------------------------------------------------------------

  async function listAttendance(filters: AttendanceFilters = {}): Promise<PagedResult<AttendanceRecord>> {
    const response = await client.get<ApiList<AttendanceRecord>>('/attendance', {
      params: {
        search: filters.search || undefined,
        employee_id: filters.employee_id || undefined,
        department_agent_id: filters.department_agent_id || undefined,
        status: filters.status || undefined,
        date_from: filters.date_from || undefined,
        date_to: filters.date_to || undefined,
        page: filters.page ?? 1,
        per_page: filters.per_page ?? 20,
      },
    })
    return {
      items: response.data.data,
      meta: parseMeta(response.data.meta, filters.page ?? 1, filters.per_page ?? 20),
    }
  }

  async function listAllAttendance(filters: AttendanceFilters = {}): Promise<AttendanceRecord[]> {
    const items: AttendanceRecord[] = []
    let page = 1
    let totalPages = 1
    do {
      const result = await listAttendance({ ...filters, page, per_page: 100 })
      items.push(...result.items)
      totalPages = Math.max(1, result.meta.total_pages)
      page += 1
    } while (page <= totalPages && page <= MAX_PAGES)
    return items
  }

  async function createAttendance(payload: AttendancePayload): Promise<AttendanceRecord> {
    const response = await client.post<ApiItem<AttendanceRecord>>('/attendance', payload)
    return response.data.data
  }

  async function updateAttendance(id: number, payload: Partial<AttendancePayload>): Promise<AttendanceRecord> {
    const response = await client.put<ApiItem<AttendanceRecord>>(`/attendance/${id}`, payload)
    return response.data.data
  }

  async function deleteAttendance(id: number): Promise<void> {
    await client.delete(`/attendance/${id}`)
  }

  async function attendanceReport(filters: AttendanceFilters = {}): Promise<AttendanceReportRow[]> {
    const response = await client.get<ApiItem<AttendanceReportRow[]>>('/attendance/report', {
      params: {
        employee_id: filters.employee_id || undefined,
        department_agent_id: filters.department_agent_id || undefined,
        status: filters.status || undefined,
        date_from: filters.date_from || undefined,
        date_to: filters.date_to || undefined,
        search: filters.search || undefined,
      },
    })
    return response.data.data
  }

  // --- Timesheets ----------------------------------------------------------------

  async function listTimesheets(filters: TimesheetFilters = {}): Promise<PagedResult<TimesheetEntry>> {
    const response = await client.get<ApiList<TimesheetEntry>>('/timesheets', {
      params: {
        search: filters.search || undefined,
        employee_id: filters.employee_id || undefined,
        department_agent_id: filters.department_agent_id || undefined,
        entry_type: filters.entry_type || undefined,
        date_from: filters.date_from || undefined,
        date_to: filters.date_to || undefined,
        page: filters.page ?? 1,
        per_page: filters.per_page ?? 20,
      },
    })
    return {
      items: response.data.data,
      meta: parseMeta(response.data.meta, filters.page ?? 1, filters.per_page ?? 20),
    }
  }

  async function listAllTimesheets(filters: TimesheetFilters = {}): Promise<TimesheetEntry[]> {
    const items: TimesheetEntry[] = []
    let page = 1
    let totalPages = 1
    do {
      const result = await listTimesheets({ ...filters, page, per_page: 100 })
      items.push(...result.items)
      totalPages = Math.max(1, result.meta.total_pages)
      page += 1
    } while (page <= totalPages && page <= MAX_PAGES)
    return items
  }

  async function createTimesheet(payload: TimesheetPayload): Promise<TimesheetEntry> {
    const response = await client.post<ApiItem<TimesheetEntry>>('/timesheets', payload)
    return response.data.data
  }

  async function deleteTimesheet(id: number): Promise<void> {
    await client.delete(`/timesheets/${id}`)
  }

  async function timesheetReport(filters: TimesheetFilters = {}): Promise<TimesheetReportRow[]> {
    const response = await client.get<ApiItem<TimesheetReportRow[]>>('/timesheets/report', {
      params: {
        employee_id: filters.employee_id || undefined,
        department_agent_id: filters.department_agent_id || undefined,
        entry_type: filters.entry_type || undefined,
        date_from: filters.date_from || undefined,
        date_to: filters.date_to || undefined,
        search: filters.search || undefined,
      },
    })
    return response.data.data
  }

  // --- Reference options ---------------------------------------------------------

  /**
   * Every agent of one role (`GET /agents?role=…`), walking the result pages so
   * the picker receives the complete list (capped at `MAX_PAGES`).
   */
  async function listAllAgentsByRole(role: string): Promise<Option[]> {
    const items: Option[] = []
    let page = 1
    let totalPages = 1
    do {
      const response = await client.get<ApiList<Option>>('/agents', {
        params: { role, is_active: 1, page, per_page: 100 },
      })
      items.push(...response.data.data)
      totalPages = Math.max(1, parseMeta(response.data.meta, page, 100).total_pages)
      page += 1
    } while (page <= totalPages && page <= MAX_PAGES)
    return items
  }

  /**
   * Departments, internal teams, and warehouses that can be set as an
   * employee's workplace (`department_agent_id`) — the same three agent roles
   * the dashboard personnel form offers. The merged list is de-duplicated by
   * agent id and sorted by name; the picker paginates it client-side.
   */
  async function listWorkplaceOptions(): Promise<Option[]> {
    const [departments, internalTeams, warehouses] = await Promise.all([
      listAllAgentsByRole('department'),
      listAllAgentsByRole('internal'),
      listAllAgentsByRole('warehouse'),
    ])

    const merged = new Map<number, Option>()
    for (const agent of [...departments, ...internalTeams, ...warehouses]) {
      if (agent === null || agent === undefined || agent.id === null || agent.id === undefined) continue
      merged.set(agent.id, { ...agent, roles: normalizeRoles(agent.roles) })
    }

    return [...merged.values()].sort((a, b) => a.name.localeCompare(b.name))
  }

  /**
   * Active currency options for the compensation section, from the documented
   * currencies API (`GET /currencies`).
   */
  async function listCurrencies(): Promise<CurrencyOption[]> {
    const response = await client.get<ApiList<CurrencyOption>>('/currencies', {
      params: { is_active: 1, page: 1, per_page: 100 },
    })
    return response.data.data
  }

  /**
   * Next free employee code (`EMP-0001`). Employee codes are generated instead
   * of typed on the create form: the highest numeric suffix in the register is
   * incremented, and the caller retries once when the code was taken.
   */
  async function nextEmployeeCode(prefix = 'EMP'): Promise<string> {
    const employees = await listAllEmployees()
    let highest = 0
    for (const employee of employees) {
      const match = employee.employee_code ? employee.employee_code.match(/(\d+)\s*$/) : null
      if (!match) continue
      const value = Number(match[1])
      if (Number.isFinite(value) && value > highest) highest = value
    }
    return `${prefix}-${String(highest + 1).padStart(4, '0')}`
  }

  // --- File registry (employee documents and scans) ------------------------------

  /** Registered files, typically scoped to one employee via `attachable_type`/`attachable_id`. */
  async function listFiles(filters: FileFilters = {}): Promise<PagedResult<UploadedFile>> {
    const response = await client.get<ApiList<UploadedFile>>('/files', {
      params: {
        attachable_type: filters.attachable_type || undefined,
        attachable_id: filters.attachable_id || undefined,
        category: filters.category || undefined,
        search: filters.search || undefined,
        page: filters.page ?? 1,
        per_page: filters.per_page ?? 50,
      },
    })
    return {
      items: response.data.data,
      meta: parseMeta(response.data.meta, filters.page ?? 1, filters.per_page ?? 50),
    }
  }

  async function uploadFile(payload: UploadFilePayload): Promise<UploadedFile> {
    const response = await client.post<ApiItem<UploadedFile>>('/files', payload)
    return response.data.data
  }

  async function deleteFile(id: number | string): Promise<void> {
    await client.delete(`/files/${id}`)
  }

  /**
   * Streams a stored file through the authenticated client: private files are
   * never reachable by URL and a plain anchor cannot carry the bearer token.
   */
  async function downloadFile(url: string, fallbackName = 'document'): Promise<void> {
    const response = await client.get<Blob>(toApiFilePath(url), { responseType: 'blob' })
    const headers = response.headers as unknown as Record<string, string | undefined>
    const disposition = String(headers['content-disposition'] ?? '')
    const nameMatch = /filename="?([^";]+)"?/i.exec(disposition)
    const fileName = nameMatch ? nameMatch[1].trim() : fallbackName

    const objectUrl = window.URL.createObjectURL(response.data)
    try {
      const anchor = document.createElement('a')
      anchor.href = objectUrl
      anchor.download = fileName
      anchor.rel = 'noreferrer'
      document.body.appendChild(anchor)
      anchor.click()
      anchor.remove()
    } finally {
      window.setTimeout(() => window.URL.revokeObjectURL(objectUrl), 0)
    }
  }

  return {
    listEmployees,
    listAllEmployees,
    getEmployee,
    createEmployee,
    updateEmployee,
    deleteEmployee,
    listEmployeeCards,
    createEmployeeCard,
    updateEmployeeCard,
    deleteEmployeeCard,
    listAttendance,
    listAllAttendance,
    createAttendance,
    updateAttendance,
    deleteAttendance,
    attendanceReport,
    listTimesheets,
    listAllTimesheets,
    createTimesheet,
    deleteTimesheet,
    timesheetReport,
    listWorkplaceOptions,
    listCurrencies,
    nextEmployeeCode,
    listFiles,
    uploadFile,
    deleteFile,
    downloadFile,
  }
}
